/**
 * The Wilcoxon signed-rank test for paired data (or one sample of differences).
 *
 * Matches `scipy.stats.wilcoxon(zero_method="wilcox")` with `method="exact"` and
 * `method="asymptotic"`. Zero differences are dropped before ranking (Wilcoxon's
 * method; Pratt's alternative ranks them and then discards their ranks). Tied |d| get
 * average ranks. T⁺ is the sum of the ranks of the positive differences — what R's
 * `wilcox.test` prints as V — and T⁻ the sum for the negative ones.
 *
 * The exact null distribution of T⁺ counts the subsets of {1, …, n} with each sum; it is
 * only exact without ties, so ties fall back to the normal approximation with
 * σ² = n(n + 1)(2n + 1)/24 − Σ(t³ − t)/48.
 */

import { averageRanks, tieGroupSizes } from "@/lib/stats/descriptive";
import { normalCdf, normalUpper } from "@/lib/stats/distributions";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";

export const EXACT_LIMIT = 25;

export interface WilcoxonRow {
  difference: number;
  /** Rank of |d| among the non-zero differences; null for a dropped zero. */
  rank: number | null;
}

export interface WilcoxonResult {
  rows: WilcoxonRow[];
  /** Pairs entered. */
  nTotal: number;
  zeros: number;
  /** Non-zero differences — the n the test uses. */
  n: number;
  tPlus: number;
  tMinus: number;
  /** min(T⁺, T⁻). */
  w: number;
  mu: number;
  sigma: number;
  /** z from T⁺ − μ, continuity-corrected toward zero if asked. */
  z: number;
  hasTies: boolean;
  pExact: number | null;
  pNormal: number;
  p: number;
  method: "exact" | "normal";
  /** |z| / √n, with n the non-zero pairs. */
  r: number;
  /** (T⁺ − T⁻) / (T⁺ + T⁻), the matched-pairs rank-biserial correlation. */
  rankBiserial: number;
}

/** Number of subsets of {1, …, n} with each sum 0 … n(n + 1)/2. */
export function signedRankCounts(n: number): number[] {
  const max = (n * (n + 1)) / 2;
  const counts = new Array<number>(max + 1).fill(0);
  counts[0] = 1;
  for (let k = 1; k <= n; k++) {
    for (let s = max; s >= k; s--) {
      counts[s] += counts[s - k];
    }
  }
  return counts;
}

function exactP(tPlus: number, n: number, alternative: Alternative): number {
  const counts = signedRankCounts(n);
  const total = 2 ** n;
  const cdf = (k: number) => {
    let sum = 0;
    for (let s = 0; s <= Math.min(k, counts.length - 1); s++) {
      sum += counts[s];
    }
    return sum / total;
  };
  const sf = (k: number) => {
    let sum = 0;
    for (let s = Math.max(0, k); s < counts.length; s++) {
      sum += counts[s];
    }
    return sum / total;
  };
  if (alternative === "less") {
    return cdf(Math.ceil(tPlus));
  }
  if (alternative === "greater") {
    return sf(Math.floor(tPlus));
  }
  return Math.min(
    1,
    2 * Math.min(sf(Math.floor(tPlus)), cdf(Math.ceil(tPlus)))
  );
}

function normalP(z: number, alternative: Alternative): number {
  if (alternative === "less") {
    return normalCdf(z);
  }
  if (alternative === "greater") {
    return normalUpper(z);
  }
  return Math.min(1, 2 * normalUpper(Math.abs(z)));
}

function correctedZ(
  tPlus: number,
  mu: number,
  sigma: number,
  continuity: boolean,
  alternative: Alternative
): number {
  const raw = (tPlus - mu) / sigma;
  if (!continuity) {
    return raw;
  }
  let sign = Math.sign(raw);
  if (alternative === "greater") {
    sign = 1;
  } else if (alternative === "less") {
    sign = -1;
  }
  return raw - (sign * 0.5) / sigma;
}

export function wilcoxonSignedRank(
  differences: readonly number[],
  options: { continuity: boolean; alternative: Alternative }
): WilcoxonResult | null {
  const nonZero = differences.filter((d) => d !== 0);
  const n = nonZero.length;
  if (n < 1) {
    return null;
  }
  const ranks = averageRanks(nonZero.map(Math.abs));
  let tPlus = 0;
  let tMinus = 0;
  for (const [i, d] of nonZero.entries()) {
    if (d > 0) {
      tPlus += ranks[i];
    } else {
      tMinus += ranks[i];
    }
  }
  const ties = tieGroupSizes(nonZero.map(Math.abs));
  const tieTerm = ties.reduce((acc, t) => acc + t ** 3 - t, 0);
  const mu = (n * (n + 1)) / 4;
  const sigma = Math.sqrt((n * (n + 1) * (2 * n + 1)) / 24 - tieTerm / 48);
  const z = correctedZ(
    tPlus,
    mu,
    sigma,
    options.continuity,
    options.alternative
  );
  const hasTies = ties.length > 0;
  const pExact =
    !hasTies && n <= EXACT_LIMIT ? exactP(tPlus, n, options.alternative) : null;
  const pNormal = normalP(z, options.alternative);
  let rankIndex = 0;
  const rows = differences.map((difference) => {
    if (difference === 0) {
      return { difference, rank: null };
    }
    const rank = ranks[rankIndex];
    rankIndex += 1;
    return { difference, rank };
  });
  return {
    rows,
    nTotal: differences.length,
    zeros: differences.length - n,
    n,
    tPlus,
    tMinus,
    w: Math.min(tPlus, tMinus),
    mu,
    sigma,
    z,
    hasTies,
    pExact,
    pNormal,
    p: pExact ?? pNormal,
    method: pExact === null ? "normal" : "exact",
    r: Math.abs(z) / Math.sqrt(n),
    rankBiserial: (tPlus - tMinus) / (tPlus + tMinus),
  };
}
